import { REGION_GEO, REGION_ORDER } from "../jawselRegions";
import { jamWIB } from "../sky";
import { BBOX, COAST_RINGS, mapPoint, viewFor, withCam, inPoly } from "./geo";
import { puff, hashN, DRIFT_CLOUDS, drawDriftCloud, drawFlock, skyAt, rgb, HOME_STARS, drawTree } from "./sprites";
import { drawCliffCached } from "./cliff";
import { drawMiniKota, drawLampuKota, adaIsi } from "./miniKota";

// Satu frame kanvas pulau melayang: langit, tebing, wilayah, isi mini, hover, burung, awan.
export function drawPulauFrame(canvas, ctx, ms, { cam, hover, stats }) {
  function polyPath(pts, v, bob = 0) {
    const [x0, y0] = mapPoint(v, pts[0][0], pts[0][1], bob);
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    for (let i = 1; i < pts.length; i++) {
      const [x, y] = mapPoint(v, pts[i][0], pts[i][1], bob);
      ctx.lineTo(x, y);
    }
    ctx.closePath();
  }

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const W = canvas.clientWidth, H = canvas.clientHeight;
  if (!W || !H) return;
  if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const t = ms / 1000;
  const v = withCam(viewFor(W, H), cam, W, H);
  const compact = v.compact;
  // Bob di-snap ke piksel perangkat agar blit cache tebing tetap tajam.
  const bob = Math.round(Math.sin(t * 0.55) * (compact ? 3 : 5) * dpr) / dpr;

  const hour = jamWIB();
  const sky = skyAt(hour);
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, rgb(sky.top));
  bg.addColorStop(1, rgb(sky.bot));
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  if (sky.dark > 0.55) {
    for (const st of HOME_STARS) {
      const a = (0.25 + 0.75 * Math.abs(Math.sin(t * st.tw + st.x * 9))) * (sky.dark - 0.5) * 1.4;
      ctx.fillStyle = `rgba(255,255,255,${Math.min(1, a).toFixed(3)})`;
      ctx.beginPath(); ctx.arc(st.x * W, st.y * H, st.r, 0, Math.PI * 2); ctx.fill();
    }
    ctx.fillStyle = "rgba(246,241,213,0.95)";
    ctx.beginPath(); ctx.arc(W * 0.86, H * 0.16, compact ? 18 : 30, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = rgb(sky.top);
    ctx.beginPath(); ctx.arc(W * 0.845, H * 0.15, compact ? 16 : 27, 0, Math.PI * 2); ctx.fill();
  }
  const cloudA = 1 - sky.dark * 0.72;
  puff(ctx, W * 0.18, H * 0.14, compact ? 30 : 54, 0.55 * cloudA);
  puff(ctx, W * 0.84, H * 0.18, compact ? 26 : 48, 0.48 * cloudA);

  // ===== TEBING PULAU (ekstrusi persis tepi hijau, lihat drawCliff) =====
  const S = Math.min(W, H);
  const DEPTH = S * (compact ? 0.085 : 0.105); // kedalaman tebing (px)
  drawCliffCached(ctx, v, W, H, dpr, DEPTH, bob);
  // --- Bongkahan kecil melayang: menempel tepi kiri/kanan layar, hanya separuh badannya yang terlihat (mengintip) ---
  const cyr = v.oy + (BBOX.h * v.s) / 2;
  const nDeb = compact ? 5 : 7;
  for (let k = 0; k < nDeb; k++) {
    const side = k % 2 ? 1 : -1;
    const bs = (compact ? 14 : 22) + hashN(k * 9.3) * (compact ? 10 : 18);
    const bx = (side < 0 ? 0 : W) + Math.sin(t * 0.5 + k * 1.9) * (compact ? 3 : 5);
    const by = cyr + (BBOX.h * v.s) * (0.52 + hashN(k * 7.7) * 0.18)
      + Math.cos(t * 0.45 + k * 1.3) * (compact ? 3 : 5);
    const mini = [[1.0, "#4A3B32", 0.0], [0.74, "#8C583A", -0.26], [0.5, "#B07D56", -0.46]];
    for (let m = 0; m < mini.length; m++) {
      const [mr, mc, my] = mini[m];
      const wob = 1 + hashN(k * 11 + m * 3) * 0.2;
      ctx.beginPath();
      ctx.ellipse(bx, by + my * bs, bs * mr * wob, bs * mr * 0.62 * wob, 0, 0, Math.PI * 2);
      ctx.fillStyle = mc;
      ctx.fill();
    }
  }
  puff(ctx, v.ox + (BBOX.w * v.s) * 0.22, v.oy + (BBOX.h * v.s) + DEPTH + (compact ? 20 : 34), compact ? 34 : 62, 0.5);
  puff(ctx, v.ox + (BBOX.w * v.s) * 0.74, v.oy + (BBOX.h * v.s) + DEPTH + (compact ? 26 : 44), compact ? 40 : 74, 0.55);

  const grass = ctx.createLinearGradient(0, v.oy, 0, v.oy + BBOX.h * v.s);
  grass.addColorStop(0, "#8ccf6a");
  grass.addColorStop(0.55, "#78b95c");
  grass.addColorStop(1, "#5b994b");
  for (let idx = 0; idx < REGION_ORDER.length; idx++) {
    const id = REGION_ORDER[idx];
    const r = REGION_GEO[id];
    polyPath(r.pts, v, bob);
    ctx.fillStyle = grass;
    ctx.fill();
    if (r.extra) {
      ctx.fillStyle = grass;
      ctx.strokeStyle = "rgba(104,162,80,0.95)";
      ctx.lineWidth = Math.max(compact ? 1.5 : 2.5, v.s * 0.0014);
      for (const pts of r.extra) { polyPath(pts, v, bob); ctx.fill(); ctx.stroke(); }
    }
    ctx.save();
    polyPath(r.pts, v, bob);
    ctx.clip();
    const xs = r.pts.map(p => p[0]), ys = r.pts.map(p => p[1]);
    const rx0 = Math.min(...xs), rx1 = Math.max(...xs), ry0 = Math.min(...ys), ry1 = Math.max(...ys);
    for (let k = 0; k < (compact ? 12 : 22); k++) {
      const [x, y] = mapPoint(v, rx0 + hashN(idx * 91 + k * 13.7) * (rx1 - rx0), ry0 + hashN(idx * 37 + k * 7.9) * (ry1 - ry0), bob);
      ctx.fillStyle = k % 3 ? "rgba(255,255,240,0.10)" : "rgba(30,70,20,0.10)";
      ctx.fillRect(x, y, Math.max(1, v.s * 0.002), Math.max(1, v.s * 0.0014));
    }
    ctx.restore();
    ctx.lineJoin = "round";
    ctx.strokeStyle = "rgba(150,150,150,0.75)";
    ctx.lineWidth = Math.max(compact ? 1 : 1.3, v.s * 0.0010);
    polyPath(r.pts, v, bob);
    ctx.stroke();
  }

  // Isi wilayah: kota yang sudah dibangun → MINIATUR LIVE tata letak aslinya (miniKota.js); wilayah kosong → hutan.
  drawMiniKota(ctx, canvas, v, bob, stats, compact);
  for (let idx = 0; idx < REGION_ORDER.length; idx++) {
    const id = REGION_ORDER[idx];
    const r = REGION_GEO[id];
    const st = stats[id] || {};
    if (adaIsi(st)) continue;
    const nt = compact ? 16 : 26;
    const xs = r.pts.map(p => p[0]), ys = r.pts.map(p => p[1]);
    const rx0 = Math.min(...xs), rx1 = Math.max(...xs), ry0 = Math.min(...ys), ry1 = Math.max(...ys);
    const placed = [];
    for (let i = 0; i < nt; i++) {
      const seedBase = idx * 3000 + i * 23;
      for (let k = 0; k < 40; k++) {
        const x = rx0 + hashN(seedBase + k * 11.3) * (rx1 - rx0), y = ry0 + hashN(seedBase + k * 13.9) * (ry1 - ry0);
        if (inPoly(x, y, r.pts)) { placed.push({ x, y }); break; }
      }
    }
    placed.sort((a2, b2) => mapPoint(v, a2.x, a2.y, bob)[1] - mapPoint(v, b2.x, b2.y, bob)[1]);
    for (const p of placed) {
      const [x, y] = mapPoint(v, p.x, p.y, bob);
      drawTree(ctx, x, y, Math.max(compact ? 3.8 : 6.0, v.s * (compact ? 0.0115 : 0.0068)));
    }
  }

  const hov = hover;
  if (hov && REGION_GEO[hov]) {
    polyPath(REGION_GEO[hov].pts, v, bob);
    ctx.fillStyle = "rgba(255,255,255,0.16)";
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = compact ? 2 : 3;
    ctx.stroke();
  }
  // ===== GARIS PANTAI TERPROYEKSI (dipakai burung & bayangan awan) =====
  let isTop = Infinity, isBot = -Infinity; // batas vertikal pulau hasil proyeksi
  const coastPath = new Path2D();
  for (const ring of COAST_RINGS) {
    ring.forEach((p, i) => {
      const [x, y] = mapPoint(v, p[0], p[1], bob);
      if (y < isTop) isTop = y; if (y > isBot) isBot = y;
      if (i) coastPath.lineTo(x, y); else coastPath.moveTo(x, y);
    });
    coastPath.closePath();
  }
  // ===== SEKAWANAN BURUNG (sesekali melintas, di bawah lapisan awan) =====
  drawFlock(ctx, t, W, isTop, isBot, compact, Math.max(0, 1 - sky.dark * 1.3));
  // ===== AWAN TIPIS MELINTAS PULAU =====
  // Bergerak pelan kiri→kanan lalu muncul lagi dari kiri. Bayangannya hanya jatuh
  // di daratan (clip ke garis pantai) agar tidak ada noda gelap di langit.
  const baseR = compact ? 26 : 46;
  const driftA = 1 - sky.dark * 0.5; // malam: tetap terlihat samar seperti awan tersinari bulan
  const drift = DRIFT_CLOUDS.slice(0, compact ? 3 : 5).map(c => {
    const r = baseR * c.r;
    const span = W + r * 4;
    return {
      r,
      x: ((c.x0 * span + t * (compact ? 9 : 14) * c.sp) % span) - r * 2,
      y: isTop + (isBot - isTop) * c.yf + Math.sin(t * 0.3 + c.ph) * 3,
    };
  });
  ctx.save();
  ctx.clip(coastPath);
  for (const c of drift) drawDriftCloud(ctx, c.x + c.r * 0.18, c.y + c.r * 0.75, c.r, 0.12 * driftA, true);
  ctx.restore();
  for (const c of drift) drawDriftCloud(ctx, c.x, c.y, c.r, 0.5 * driftA);
  if (sky.dark > 0.01) {
    ctx.fillStyle = `rgba(9,13,44,${(sky.dark * 0.38).toFixed(3)})`;
    ctx.fillRect(0, 0, W, H);
  }
  drawLampuKota(ctx, bob, sky.dark, ms);   // lampu miniatur kota menyala di atas selubung malam
}
